// Cmd+K palette — fastest path to logging an event.
// Type "草莓 RH 售出 10" → enter, and the SKU + location + action + qty are
// parsed and logged in one keystroke. Falls back to fuzzy SKU search if the
// input isn't a complete command.

import { useEffect, useMemo, useRef, useState } from 'react';
import { displayName } from '../lib/products.ts';
import { LOCATIONS, isKitchen, locationLabel } from '../lib/locations.ts';

// Accept friendly kitchen tokens: CKRH / CK(RH) / CK_RH all → CK_RH. Bare CK
// is ambiguous post-split, so it is NOT auto-resolved (produce needs a kitchen).
function normalizeLocToken(upper) {
  const cleaned = upper.replace(/[()]/g, '').replace('-', '_');
  if (cleaned === 'CKRH' || cleaned === 'CK_RH') return 'CK_RH';
  if (cleaned === 'CKNY' || cleaned === 'CK_NY') return 'CK_NY';
  return LOCATIONS.includes(cleaned) ? cleaned : null;
}
const ACTION_WORDS = {
  '生产': 'produced', '产': 'produced', 'produce': 'produced', 'p': 'produced',
  '售出': 'sold',     '售': 'sold',     '卖': 'sold',         'sell': 'sold', 's': 'sold',
  '报废': 'wasted',   '废': 'wasted',   'waste': 'wasted',    'w': 'wasted',
  '调拨': 'transferred', '调': 'transferred', 'transfer': 'transferred', 't': 'transferred'
};

// Parse "input" into { match, location?, action?, qty?, sku? }.
// Strategy: split on spaces; classify each token as LOC / ACTION / NUMBER / NAME.
// Whatever's left after subtracting those is the SKU search query.
function parseCommand(input, products) {
  const tokens = input.trim().split(/\s+/).filter(Boolean);
  let location = null, action = null, qty = null;
  const nameTokens = [];
  for (const t of tokens) {
    const upper = t.toUpperCase();
    const loc = normalizeLocToken(upper);
    if (loc) { location = loc; continue; }
    if (ACTION_WORDS[t.toLowerCase()]) { action = ACTION_WORDS[t.toLowerCase()]; continue; }
    if (ACTION_WORDS[t])           { action = ACTION_WORDS[t]; continue; }
    if (/^\d+$/.test(t))            { qty = parseInt(t, 10); continue; }
    nameTokens.push(t);
  }
  const query = nameTokens.join(' ').toLowerCase();
  // Fuzzy SKU match — substring on name/variant/category.
  const matches = (products || [])
    .filter(p => {
      if (!query) return true;
      const hay = `${p.name} ${p.variant || ''} ${p.category || ''}`.toLowerCase();
      return hay.includes(query);
    })
    .slice(0, 8);
  return { matches, location, action, qty, query };
}

export const CommandPalette = ({ products, onLog, onTransfer, onOpenDetail, onClose }) => {
  const [input, setInput] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const parsed = useMemo(() => parseCommand(input, products), [input, products]);

  // Reset cursor when the result list changes.
  useEffect(() => { setActiveIdx(0); }, [parsed.matches.length]);

  // Esc closes; arrows move; enter fires.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx(i => Math.min(parsed.matches.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx(i => Math.max(0, i - 1));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [parsed.matches.length, onClose]);

  async function fire() {
    const product = parsed.matches[activeIdx];
    if (!product) { setErr('未匹配到 SKU'); return; }
    // If user typed a full command (action + location + qty), log it.
    // Otherwise just open the detail slide-over for that SKU.
    if (parsed.action && parsed.location && parsed.qty) {
      setSubmitting(true); setErr('');
      try {
        if (parsed.action === 'transferred') {
          setErr('调拨请用 SKU 单元格点击 — 命令面板暂不支持目的地选择');
          setSubmitting(false);
          return;
        }
        // Produce is kitchen-only and ambiguous without one (CK_RH vs CK_NY).
        if (parsed.action === 'produced' && !isKitchen(parsed.location)) {
          setErr('生产请指明厨房：CKRH 或 CKNY');
          setSubmitting(false);
          return;
        }
        await onLog({ productId: product.id, kind: parsed.action, location: parsed.location, qty: parsed.qty });
        onClose();
      } catch (e) {
        setErr(e?.message || '失败');
        setSubmitting(false);
      }
    } else {
      onOpenDetail(product.id);
      onClose();
    }
  }

  return (
    <div className="inv-palette-backdrop" onClick={onClose}>
      <div className="inv-palette" onClick={(e) => e.stopPropagation()}>
        <input ref={inputRef}
               type="text"
               value={input}
               onChange={(e) => setInput(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') fire(); }}
               placeholder="搜 SKU 或直接录入: 草莓 RH 售出 10"
               disabled={submitting} />
        <div className="inv-palette-suggest">
          {parsed.matches.length === 0 ? (
            <div className="inv-palette-item">
              <span className="cat">没有匹配的产品</span>
            </div>
          ) : parsed.matches.map((p, i) => (
            <div key={p.id}
                 className={`inv-palette-item ${i === activeIdx ? 'active' : ''}`}
                 onClick={() => { setActiveIdx(i); fire(); }}
                 onMouseEnter={() => setActiveIdx(i)}>
              <span className="name" title={p.name}>{displayName(p)}</span>
              <span className="cat">{p.category || '—'}</span>
            </div>
          ))}
        </div>
        {err && <div className="inv-palette-hint" style={{ color: 'var(--vava-red)' }}>{err}</div>}
        <div className="inv-palette-hint">
          <span>↑↓ 选择</span>
          <span><kbd>↵</kbd> 确认</span>
          <span><kbd>esc</kbd> 关闭</span>
          {(parsed.action || parsed.location || parsed.qty) && (
            <span style={{ marginLeft: 'auto' }}>
              {parsed.action && `${parsed.action} `}
              {parsed.location && `@${parsed.location} `}
              {parsed.qty != null && `×${parsed.qty}`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
